/**
 * Every assumption the model runs on, editable in place.
 *
 * Laid out the way the spreadsheet is — per-month curves as a month grid,
 * matrices as matrices — so someone who owns the sheet can find their number.
 * Edits are session-only; config/scienceModel.js is the checked-in plan.
 */

import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui'
import {
  PLANS, PLAN_LABELS, BOOKS, BOOK_LABELS, MOLECULES, MODEL_MONTHS,
} from '../../config/scienceModel'
import { addMonths } from '../../lib/scienceModel'
import { monthLabel, formatPercent } from '../../lib/scienceModelView'

const MOLECULE_LABELS = { tirzepatide: 'Tirzepatide', semaglutide: 'Semaglutide' }

/**
 * A number field. `percent` shows 0.65 as 65 and stores it back as 0.65, so
 * nobody has to type a fraction to mean a percentage.
 */
function NumberField({ value, onChange, percent = false, step, className = '', ariaLabel }) {
  // The text being typed, held apart from the model until it is a number, so
  // clearing a field to retype it does not snap the old value back.
  const [draft, setDraft] = useState(null)
  const shown = value == null ? '' : percent ? +(value * 100).toFixed(4) : value
  return (
    <input
      type="number"
      aria-label={ariaLabel}
      className={`h-8 w-full min-w-[4.5rem] rounded-md border border-input bg-background px-2 text-right text-xs tabular-nums text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${className}`}
      value={draft ?? shown}
      step={step ?? (percent ? 1 : 'any')}
      onChange={(e) => {
        const raw = e.target.value
        setDraft(raw)
        const n = Number(raw)
        if (raw === '' || !Number.isFinite(n)) return
        onChange(percent ? n / 100 : n)
      }}
      onBlur={() => setDraft(null)}
    />
  )
}

function Section({ title, description, children }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
        {description && <CardDescription className="text-xs">{description}</CardDescription>}
      </CardHeader>
      <CardContent className="overflow-x-auto">{children}</CardContent>
    </Card>
  )
}

const TH = 'px-1.5 py-1 text-left text-xs font-medium text-muted-foreground whitespace-nowrap'
const TD = 'px-1 py-1'

/** A per-month curve grid: one row per curve, one column per model month. */
function MonthGrid({ a, rows, onEdit }) {
  const months = Array.from({ length: MODEL_MONTHS }, (_, m) => addMonths(a.startMonth, m))
  return (
    <table className="w-full border-separate border-spacing-0">
      <thead>
        <tr>
          <th className={`${TH} sticky left-0 bg-card`} />
          {months.map(month => <th key={month} className={`${TH} text-right`}>{monthLabel(month)}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr key={row.label}>
            <td className={`${TD} sticky left-0 bg-card pr-3 text-xs whitespace-nowrap`}>{row.label}</td>
            {months.map((month, m) => (
              <td key={month} className={TD}>
                {row.skip?.(m) ? (
                  <span className="block text-right text-xs text-muted-foreground">—</span>
                ) : (
                  <NumberField
                    ariaLabel={`${row.label} ${monthLabel(month)}`}
                    value={row.path.reduce((o, k) => o?.[k], a)?.[m]}
                    percent={row.percent}
                    onChange={v => onEdit([...row.path, m], v)}
                  />
                )}
              </td>
            ))}
          </tr>
        ))}
        {rows.some(r => r.sumGroup) && (
          <tr>
            <td className={`${TD} sticky left-0 bg-card pr-3 text-xs text-muted-foreground whitespace-nowrap`}>Price-book weights total</td>
            {months.map((month, m) => {
              const total = BOOKS.reduce((sum, book) => sum + (a.bookWeights[book][m] || 0), 0)
              const off = Math.abs(total - 1) > 1e-6
              return (
                <td key={month} className={`${TD} text-right text-xs tabular-nums ${off ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                  {formatPercent(total, 0)}
                </td>
              )
            })}
          </tr>
        )}
      </tbody>
    </table>
  )
}

function ShareInputs({ label, keys, labels, values, onEdit, path }) {
  const total = keys.reduce((sum, key) => sum + (values[key] || 0), 0)
  const off = Math.abs(total - 1) > 1e-6
  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {keys.map(key => (
          <label key={key} className="space-y-1">
            <span className="block text-xs">{labels[key]} %</span>
            <NumberField value={values[key]} percent onChange={v => onEdit([...path, key], v)} ariaLabel={`${label} ${labels[key]}`} />
          </label>
        ))}
      </div>
      <div className={`text-xs ${off ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
        Total {formatPercent(total, 0)}{off && ' — should be 100%'}
      </div>
    </div>
  )
}

export default function AssumptionsEditor({ a, onEdit, sheetPrices, priceSources, livePrices }) {
  const pricesDescription = livePrices?.checked
    ? 'Per charge, by price book; a 3-month plan is one charge. Live from Manifold — where several products share a cell (Injectable and Oral, dose levels) it is their average weighted by live subscriptions. A cell that differs from the sheet shows the sheet price under it.'
    : 'Per charge, by price book; a 3-month plan is one charge. From the sheet: live Manifold prices are unavailable.'

  return (
    <div className="space-y-4">
      <Section
        title="Acquisition"
        description="Customers = paid spend ÷ paid CAC + organic spend ÷ organic CAC. Growth compounds month one's spend."
      >
        <div className="mb-3 max-w-xs">
          <label className="space-y-1">
            <span className="block text-xs">Month-one ad spend ($)</span>
            <NumberField value={a.acquisition.startingSpend} step={10000} onChange={v => onEdit(['acquisition', 'startingSpend'], v)} ariaLabel="Month-one ad spend" />
          </label>
        </div>
        <MonthGrid
          a={a}
          onEdit={onEdit}
          rows={[
            { label: 'Spend growth %', path: ['acquisition', 'spendGrowth'], percent: true, skip: m => m === 0 },
            { label: 'Paid share %', path: ['acquisition', 'paidShare'], percent: true },
            { label: 'CAC — paid $', path: ['acquisition', 'cacPaid'] },
            { label: 'CAC — organic $', path: ['acquisition', 'cacOrganic'] },
            { label: 'Web weight %', path: ['bookWeights', 'web'], percent: true, sumGroup: true },
            { label: 'Sales Team weight %', path: ['bookWeights', 'sales_team'], percent: true, sumGroup: true },
            { label: 'Reddit weight %', path: ['bookWeights', 'reddit'], percent: true, sumGroup: true },
          ]}
        />
      </Section>

      <Section title="Mix" description="Share of new customers on each plan length, and on each molecule.">
        <div className="grid gap-6 lg:grid-cols-2">
          <ShareInputs label="Plan split" keys={PLANS} labels={PLAN_LABELS} values={a.planSplit} path={['planSplit']} onEdit={onEdit} />
          <ShareInputs label="Molecule mix" keys={MOLECULES} labels={MOLECULE_LABELS} values={a.moleculeMix} path={['moleculeMix']} onEdit={onEdit} />
        </div>
      </Section>

      <Section title="Prices" description={pricesDescription}>
        <div className="grid gap-6 xl:grid-cols-2">
          {MOLECULES.map(molecule => (
            <table key={molecule} className="w-full">
              <thead>
                <tr>
                  <th className={TH}>{MOLECULE_LABELS[molecule]}</th>
                  {BOOKS.map(book => <th key={book} className={`${TH} text-right`}>{BOOK_LABELS[book]}</th>)}
                </tr>
              </thead>
              <tbody>
                {PLANS.map(plan => (
                  <tr key={plan}>
                    <td className={`${TD} text-xs`}>{PLAN_LABELS[plan]}</td>
                    {BOOKS.map(book => {
                      const value = a.prices[molecule][plan][book]
                      const sheet = sheetPrices?.[molecule]?.[plan]?.[book]
                      const source = priceSources?.[molecule]?.[plan]?.[book]
                      return (
                        <td key={book} className={`${TD} align-top`}>
                          <NumberField
                            value={value}
                            onChange={v => onEdit(['prices', molecule, plan, book], v)}
                            ariaLabel={`${MOLECULE_LABELS[molecule]} ${PLAN_LABELS[plan]} ${BOOK_LABELS[book]}`}
                          />
                          {livePrices?.checked && source === 'sheet' && (
                            <span className="block text-right text-[10px] text-muted-foreground">sheet · no live price</span>
                          )}
                          {sheet != null && Math.abs(value - sheet) >= 0.005 && (
                            <span className="block text-right text-[10px] text-muted-foreground tabular-nums">sheet ${sheet.toLocaleString('en-US')}</span>
                          )}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          ))}
        </div>
      </Section>

      <Section
        title="Retention"
        description="Share of a cohort that pays in each month after buying. Month 0 is the purchase; a 3-month plan only pays on its renewal months."
      >
        <table className="w-full">
          <thead>
            <tr>
              <th className={TH} />
              {a.retention.monthly.map((_, age) => <th key={age} className={`${TH} text-right`}>M{age}</th>)}
            </tr>
          </thead>
          <tbody>
            {PLANS.map(plan => (
              <tr key={plan}>
                <td className={`${TD} pr-3 text-xs whitespace-nowrap`}>{PLAN_LABELS[plan]}</td>
                {a.retention[plan].map((rate, age) => (
                  <td key={age} className={TD}>
                    <NumberField value={rate} percent onChange={v => onEdit(['retention', plan, age], v)} ariaLabel={`${PLAN_LABELS[plan]} retention month ${age}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <div className="grid gap-4 lg:grid-cols-3">
        <Section title="Unit costs" description="Product per charge by plan; shipping and doctor visit per paying customer per month.">
          <div className="grid grid-cols-2 gap-2">
            {PLANS.map(plan => (
              <label key={plan} className="space-y-1">
                <span className="block text-xs">Product — {PLAN_LABELS[plan]} $</span>
                <NumberField value={a.unitCosts.product[plan]} onChange={v => onEdit(['unitCosts', 'product', plan], v)} ariaLabel={`Product cost ${PLAN_LABELS[plan]}`} />
              </label>
            ))}
            <label className="space-y-1">
              <span className="block text-xs">Shipping $</span>
              <NumberField value={a.unitCosts.shipping} onChange={v => onEdit(['unitCosts', 'shipping'], v)} ariaLabel="Shipping cost" />
            </label>
            <label className="space-y-1">
              <span className="block text-xs">Doctor visit $</span>
              <NumberField value={a.unitCosts.doctorVisit} onChange={v => onEdit(['unitCosts', 'doctorVisit'], v)} ariaLabel="Doctor visit cost" />
            </label>
          </div>
        </Section>

        <Section title="Rates" description="Refunds on revenue; merchant and management on income; agency on ad spend.">
          <div className="grid grid-cols-2 gap-2">
            {[
              ['refunds', 'Refunds'], ['cogsOther', 'COGS other'], ['merchant', 'Merchant fees'],
              ['agency', 'Agency fees'], ['management', 'Management fee'], ['commission', 'Commission'],
            ].map(([key, label]) => (
              <label key={key} className="space-y-1">
                <span className="block text-xs">{label} %</span>
                <NumberField value={a.rates[key]} percent step={0.1} onChange={v => onEdit(['rates', key], v)} ariaLabel={label} />
              </label>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Agency is fixed at $20K and $25K in months 1–2, and management at $75K in months 1–3, as in the sheet.
          </p>
        </Section>

        <Section title="Fixed monthly" description="Operating costs no system records, so every row uses these.">
          <div className="grid grid-cols-2 gap-2">
            {[
              ['software', 'Software'], ['marketingFees', 'Marketing fees'], ['legal', 'Legal & accounting'],
              ['office', 'Office'], ['supplies', 'Supplies'], ['payroll', 'Payroll'], ['rent', 'Rent'], ['insurance', 'Insurance'],
            ].map(([key, label]) => (
              <label key={key} className="space-y-1">
                <span className="block text-xs">{label} $</span>
                <NumberField value={a.fixedMonthly[key]} onChange={v => onEdit(['fixedMonthly', key], v)} ariaLabel={label} />
              </label>
            ))}
          </div>
        </Section>
      </div>
    </div>
  )
}
